import { Check, ClockFading, X } from 'lucide-react'
/**
 * Sticky top bar + property strip + header card (design §2-§4).
 * Action/menu visibility derives ONLY from `can.*` flags and the derived
 * status (v1's getLandlordGroupActions map — data-map §7); the Share item
 * rides `can.sendUpdateLink` (recorded decision, data-map §2: posting
 * `share` fails closed and `sendUpdateLink` 403s non-superadmins).
 */
import type { ReactNode } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { Avatar, AvatarFallback, AvatarImage } from '#app/components/ui/avatar'
import { deriveReportAction } from '#app/utils/application-detail-v2/derive'
import {
	findFacePhoto,
	hasAiPredictions,
} from '#app/utils/application-detail-v2/display'
import {
	deriveRiskFlagLists,
	mentionsBankAccount,
} from '#app/utils/application-detail-v2/report-display'
import {
	preserveReturnTo,
	resolveBackTarget,
} from '#app/utils/application-detail-v2/return-to'
import type { ApplicationDetailData } from '#app/utils/application-detail-v2/types'
import {
	getLandlordGroupActions,
	isDecisionLocked,
	toApplicationStatusForLandlordActions,
} from '#app/utils/landlord-application-actions'
import { cn } from '#app/utils/misc'
import { HeaderBadges } from './BadgePopovers'
import { KebabMenu } from './HeaderMenus'
import { HeaderScorePanel, ScorePendingPanel } from './HeaderScorePanel'
import { PreScreenFlagBanner } from './PreScreenFlagBanner'
import { PropertyStrip } from './PropertyStrip'
import { UpsellBanner } from './UpsellBanner'
import { DownloadMenu } from './downloads/DownloadMenu'
import type { DownloadsApi } from './downloads/useDownloads'
import { DETAIL_CONTAINER } from './primitives'

export interface HeaderActionHandlers {
	/** Opens the shared AddApplicantDialog — the same component and
	 * `addApplicant` intent the combined page uses. */
	onAddApplicant?: () => void
	onApprove?: () => void
	onDecline?: () => void
	onWithdraw?: () => void
	onReinstate?: () => void
	onRequestGuarantor?: () => void
	onRequestBankAccount?: () => void
	/** Opens the Request Documents dialog (client 2026-08-27). */
	onRequestDocuments?: () => void
	/** Opens the Reach Out dialog — same component the dashboard uses. */
	onReachOut?: () => void
	onSendUpdateLink?: () => void
	onDeleteApplication?: () => void
	onOpenTimeline?: () => void
	onProceedToFullScreen?: () => void
	onRegenerateReport?: () => void
	/** Opens the credit top-up (RenewCredits) dialog — rides `data.billing`. */
	onOpenTopup?: () => void
	/** Clicking the header avatar opens the same ID-verification dialog as the
	 * tile's "View". */
	onViewIdVerification?: () => void
}

export function ApplicationHeader({
	data,
	handlers = {},
	downloadsApi,
	profileMenu,
}: {
	data: ApplicationDetailData
	handlers?: HeaderActionHandlers
	/** Step 4b wiring — the menu hides without it (static previews/tests). */
	downloadsApi?: DownloadsApi
	/** Account avatar + menu for the top bar (this route hides the shared
	 * SiteHeader). Injected by DetailPageController rather than rendered
	 * here because it reads the ROOT loader, and every data-router hook
	 * throws under the bare MemoryRouter these components are tested in —
	 * same reason downloadsApi is a prop. Absent in tests → nothing shows. */
	profileMenu?: ReactNode
}) {
	const navigate = useNavigate()
	const [searchParams] = useSearchParams()
	const { application, can, property, screeningKind, steps, billing } = data
	const propertyId = application.propertyId ?? ''
	// Only show the score box once a real report exists (TT2-502). Before the
	// applicant submits / while generation runs or has failed, the report has no
	// predictions and the score derives to 0 — a misleading "0/100". Gate on the
	// SAME signal the page body uses (hasAiPredictions) so header and body agree.
	const hasReport = hasAiPredictions(data.results?.aiReportData ?? null)
	const reportFailed = data.results?.aiProcessingStatus === 'failed'
	const reportSkipped = data.results?.aiProcessingStatus === 'skipped'
	// Same lists the body's Risk Flags card derives — one source, so the banner
	// and the section can never disagree.
	const preScreenFlags = deriveRiskFlagLists(data.results?.aiReportData ?? null)
	// TT-12: submitted but the report is held on credits — the score panel must
	// say "on hold", not the generic "score appears once screening is complete"
	// (screening IS complete here).
	const reportHeld =
		data.application.billingHold &&
		!hasReport &&
		!reportFailed &&
		!reportSkipped

	// v1 action availability keyed off the DERIVED status (v1:4554-4571),
	// ANDed with the matching can.* capability (data-map §7). Terminal
	// statuses return the empty action set (DETAIL-V2-DECISION-RULES.md) —
	// the status chip takes the buttons' place.
	const actions = getLandlordGroupActions(
		toApplicationStatusForLandlordActions(application.derivedStatus),
	)
	// Request actions lock on ANY recorded decision — an accepted application
	// keeps Withdraw (the rule 1 carve-out) but must not re-open requests, so
	// this can't be derived from the actions map anymore.
	const decisionLocked = isDecisionLocked(application.derivedStatus)
	// Owner 2026-07-17: THIS applicant's recorded decision hides its own
	// matching button here (the status chip already says it; re-accepting an
	// accepted member is refused by the matrix anyway). The combined header
	// applies the member-aware version — an action hides only once EVERY
	// member carries it (deriveHouseholdFullyDecided, client 2026-07-21).
	const showAccept =
		actions.accept && can.approve && application.derivedStatus !== 'accepted'
	const showDecline =
		actions.decline && can.decline && application.derivedStatus !== 'declined'
	// Report (re)generation: 'regenerate' for a finished+undecided report,
	// 'generate' for one whose generation FAILED (rule 3) — the failed path that
	// previously left the landlord with no action. null hides the entry. The
	// resource endpoint re-checks every guard.
	const reportAction = deriveReportAction({
		canRegenerate: data.canRegenerate,
		derivedStatus: application.derivedStatus,
		aiProcessingStatus: data.results?.aiProcessingStatus,
	})
	const kebabItems = {
		// "Add Applicant" — the combined page's invite, surfaced on the
		// individual page too (client 2026-07-28). Available on SOLO and grouped
		// applications alike: on a solo entry the invite CREATES the groupId
		// (screening-invitations.server.ts), which is how a single application
		// grows into a household from here.
		//
		// Carries the SAME Rule 2 carve-out as Request Guarantor below — NOT
		// decision-locked, because a joining member re-opens a decided household
		// (rule 6); the server's areAllApplicantsDecided 409 was removed to
		// match. Suppressed on a GUARANTOR's own report: the intent anchors to
		// the household primary, so offering it from a guarantor's page would
		// invite a co-applicant into a household the page isn't representing.
		addApplicant:
			can.addApplicant && application.applicationType !== 'guarantor',
		// Rule 2 carve-out (client 2026-07-21): Request Guarantor stays
		// available on decided applications (a joining guarantor re-opens the
		// household); the bank request keeps the rule-2 lock — it can reset
		// status, so the server still 409s it on decided members.
		requestGuarantor: can.requestGuarantor,
		requestBankAccount: !decisionLocked && can.requestBankAccount,
		// Same rule-2 lock as the bank request: a document request re-opens
		// the application for resubmission, so decided members 409 it.
		requestDocuments: !decisionLocked && can.requestDocuments,
		editProperty: data.canEditProperty && Boolean(propertyId),
		// Reach Out is pure contact (tel:/sms:) — no permission, no lock.
		reachOut: true,
		sendUpdateLink: can.sendUpdateLink,
		withdraw: actions.withdraw && can.withdraw,
		reinstate: application.derivedStatus === 'withdrawn' && can.reinstate,
		reportAction,
		// Soft-delete — gated purely on the permission; a record can be removed
		// at any status (V2-GAPS-AND-PDF-PLAN §D).
		deleteApplication: can.deleteApplication,
	}
	const showKebab = Object.values(kebabItems).some(Boolean)

	// Same loose match as the ID-verification modal — the avatar and the
	// modal must resolve the SAME photo.
	const facePhoto = findFacePhoto(data.veriffDocuments)?.url
	// The avatar opens the ID-verification dialog when there's something to show
	// (mirrors the tile, which is enabled once Veriff documents exist).
	const canViewId =
		data.veriffDocuments.length > 0 && Boolean(handlers.onViewIdVerification)
	return (
		<header>
			{/* Top bar (design §2) — sticky per product convention. */}
			<div className="sticky top-0 z-40 border-b border-[#EDEDEB] bg-[#fbfdfd] shadow-[0px_4px_4px_0px_#0000000D]">
				<div
					className={cn(
						DETAIL_CONTAINER,
						'flex flex-wrap items-center justify-between gap-3 py-3',
					)}
				>
					<div className="flex min-w-0 items-center gap-3">
						{/* The round arrow is the only control; "Report" is a page title,
						    not a link (client 2026-08-27). */}
						<button
							type="button"
							aria-label="Back to applications"
							className="flex shrink-0 cursor-pointer items-center justify-center rounded-full transition-opacity hover:opacity-70"
							onClick={() => {
								// Origin-aware back (client 2026-07-24, supersedes the
								// 2026-07-17 "always property list"): return to the exact
								// filtered/paged list the viewer came from (?returnTo,
								// preserved across every applicant switch / combined hop),
								// else the All Applications list (return-to.ts).
								void navigate(resolveBackTarget(searchParams))
							}}
						>
							{/* The glyph carries its own circle — no border here. */}
							<img
								src="/img/circular-back-icon.svg"
								alt=""
								className="size-6 shrink-0"
							/>
						</button>
						{/* Figma (2026-09-07): the bar carries ONLY the title — the
						    applicant name and status chip are gone here, matching the
						    combined bar; the header card below names the applicant. */}
						<span className="font-calgary shrink-0 text-[20px] leading-tight font-medium text-[#1F1E1D] lg:text-[30px]">
							Report
						</span>
					</div>
					<div className="flex flex-wrap items-center justify-start gap-2 sm:gap-3">
						{billing ? (
							handlers.onOpenTopup ? (
								<button
									type="button"
									onClick={handlers.onOpenTopup}
									className="inline-flex h-11 cursor-pointer items-center gap-2 rounded-full border border-[#ECECEA] bg-white px-4 text-sm text-[#32302F] transition-colors hover:bg-[#F6F7F5]"
								>
									<img src="/img/coin.svg" alt="" />
									<span className="font-semibold">{billing.balance}</span>
									<span className="text-[#747474]">Credits</span>
								</button>
							) : (
								<span className="inline-flex h-11 items-center gap-2 rounded-full border border-[#ECECEA] bg-white px-4 text-sm text-[#32302F]">
									<img src="/img/coin.svg" alt="" />
									<span className="font-semibold">{billing.balance}</span>
									<span className="text-[#747474]">Credits</span>
								</span>
							)
						) : null}
						{/* Figma order (2026-09-07): Timeline BEFORE Download — same
						    order as the combined top bar. */}
						<button
							type="button"
							onClick={handlers.onOpenTimeline}
							className="flex h-11 cursor-pointer items-center gap-2 rounded-full border border-[#ECECEA] bg-white px-4 text-sm font-medium text-[#32302F] transition-colors hover:bg-[#F6F7F5]"
						>
							<ClockFading className="size-4 text-[#747474]" />
							Timeline
						</button>
						{can.download && downloadsApi ? (
							<DownloadMenu
								// Data-map §8: items render strictly from the availability
								// flags (Report + All Files; the ETL sheet rides inside the ZIP).
								downloads={data.downloads}
								api={downloadsApi}
								// Nothing to download until a report exists — disable the
								// button before submit / while processing / on failure
								// (same `hasReport` gate as the score box).
								disabled={!hasReport}
							/>
						) : null}
						{showAccept ? (
							<button
								type="button"
								onClick={handlers.onApprove}
								className="flex h-11 cursor-pointer items-center gap-2 rounded-full bg-green-50 px-4 text-sm font-medium text-green-600 transition-colors hover:bg-green-100"
							>
								<Check className="size-4" />
								Accept
							</button>
						) : null}
						{showDecline ? (
							<button
								type="button"
								onClick={handlers.onDecline}
								className="flex h-11 cursor-pointer items-center gap-2 rounded-full bg-red-50 px-4 text-sm font-medium text-red-500 transition-colors hover:bg-red-100"
							>
								<X className="size-4" />
								Decline
							</button>
						) : null}
						{/* The actions kebab moved DOWN into the property strip (client
							2026-08-27); the account menu takes its place here, since the
							v2 route hides the shared SiteHeader. */}
						{profileMenu}
					</div>
				</div>
			</div>

			{/* Property strip (design §3). */}
			<PropertyStrip
				property={property}
				groupMembers={data.groupMembers}
				propertyId={propertyId}
				currentName={application.applicantName}
				currentApplicationId={application.id}
				pendingInvitations={data.pendingInvitations}
				screeningKind={screeningKind}
				view="individual"
				onViewChange={(view) => {
					// Combined is a sibling ROUTE (COMBINED-V2-DATA-MAP §1); preserve
					// the origin so back still returns to the source list.
					if (view === 'combined') {
						void navigate(
							preserveReturnTo(
								`/properties/${propertyId}/applications/${application.id}/v2/combined`,
								searchParams,
							),
						)
					}
				}}
				actions={
					showKebab ? (
						<KebabMenu
							items={kebabItems}
							onAddApplicant={handlers.onAddApplicant}
							onRequestGuarantor={handlers.onRequestGuarantor}
							onRequestBankAccount={handlers.onRequestBankAccount}
							onRequestDocuments={handlers.onRequestDocuments}
							onEditProperty={() =>
								void navigate(`/properties/${propertyId}/edit`)
							}
							onReachOut={handlers.onReachOut}
							onSendUpdateLink={handlers.onSendUpdateLink}
							onWithdraw={handlers.onWithdraw}
							onReinstate={handlers.onReinstate}
							onRegenerateReport={handlers.onRegenerateReport}
							onDeleteApplication={handlers.onDeleteApplication}
						/>
					) : null
				}
			/>

			{/* Pre-screen only: the flags repeated above the applicant card
			    (client 2026-08-27). Full screening already surfaces them in the
			    body section, so it stays untouched. */}
			{screeningKind === 'pre_screening' ? (
				<PreScreenFlagBanner
					red={preScreenFlags.red}
					yellow={preScreenFlags.yellow}
					onRequestBankAccount={
						can.requestBankAccount &&
						mentionsBankAccount([
							...preScreenFlags.red,
							...preScreenFlags.yellow,
						])
							? handlers.onRequestBankAccount
							: undefined
					}
				/>
			) : null}

			{/* Header card (design §4). */}
			<div className={cn(DETAIL_CONTAINER, 'mt-6')}>
				<div className="flex flex-wrap items-center gap-4 rounded-3xl bg-[#fbfdfd] p-5 shadow-[0px_0px_16px_0px_#0000000A,0px_0px_4px_0px_#0000000A] sm:gap-6 sm:p-7 xl:flex-nowrap">
					<div className="flex min-w-0 items-center gap-4 sm:gap-6 xl:flex-1">
						{(() => {
							const avatar = (
								<Avatar className="size-20 border-2 border-[#32302F] bg-[#F4F4F2] sm:size-26">
									<AvatarImage
										src={facePhoto}
										alt={application.applicantName}
										className="object-cover object-center"
									/>
									<AvatarFallback className="bg-[#F4F4F2] text-2xl font-semibold text-[#32302F]">
										{application.applicantName.charAt(0).toUpperCase()}
									</AvatarFallback>
								</Avatar>
							)
							return canViewId ? (
								<button
									type="button"
									onClick={handlers.onViewIdVerification}
									title="View ID verification"
									aria-label="View ID verification"
									className="cursor-pointer rounded-full transition-shadow hover:ring-2 hover:ring-[#32302F]/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32302F]"
								>
									{avatar}
								</button>
							) : (
								avatar
							)
						})()}
						<div className="min-w-0">
							{/* Never clip the name: a hard slice cut 4-word names down to two
							    and read as the real name (no ellipsis). Long names wrap
							    instead — same `wrap-break-word` rule the FieldBox values use. */}
							<h1 className="text-[25px] leading-tight font-medium wrap-break-word text-[#1F1E1D] md:text-[30px] lg:text-[40px]">
								{application.applicantName}
							</h1>
							{/* Chips with hover popovers (exports 7-9, Step 4b). */}
							<HeaderBadges propertyStep={steps.property} />
						</div>
					</div>
					{/* Header right: no report yet → the not-generated panel for BOTH
					    kinds (client 2026-09-07 — pre-screen used to show the upsell
					    before anything was submitted); full + report = score;
					    pre-screen + report = upsell. Bank=No (`skipped`): nothing. */}
					{!reportSkipped && (screeningKind === 'full' || !hasReport) ? (
						<div className="flex w-full min-w-0 justify-end xl:w-auto xl:shrink-0">
							{hasReport ? (
								<HeaderScorePanel
									aiReportData={data.results?.aiReportData ?? null}
									applicationScore={application.score}
									applicationType={application.applicationType}
									scoreDisplay={data.scoreDisplay}
									guarantorRequests={data.guarantorRequests}
									// Gate the popover CTAs the same way the header buttons gate:
									// approve while not already accepted, request-guarantor on
									// permission alone (rule 2 carve-out below).
									onApprove={showAccept ? handlers.onApprove : undefined}
									// Rule 2 carve-out (client 2026-07-21): reachable on
									// decided applications too — matches the kebab item.
									onRequestGuarantor={
										can.requestGuarantor
											? handlers.onRequestGuarantor
											: undefined
									}
									// TT-42: cancel a live guarantor invite from the status popover —
									// same gating shape as the other CTAs (open decision + permission).
									cancelInvite={
										!decisionLocked && can.cancelInvitation
											? { applicationId: application.id }
											: undefined
									}
								/>
							) : (
								<ScorePendingPanel
									failed={reportFailed}
									held={reportHeld}
									applicantName={application.applicantName}
								/>
							)}
						</div>
					) : null}
					{/* Excel applications.fullscreen: Viewer = No — hide the
					    Proceed CTA (and its marketing banner) when the actor
					    cannot initiate a Full Screen spend. */}
					{screeningKind === 'pre_screening' &&
					data.canFullscreen &&
					!reportSkipped &&
					hasReport ? (
						<div className="min-w-0 flex-1 max-[1050px]:basis-auto max-[1000px]:basis-full">
							<UpsellBanner
								onProceedToFullScreen={handlers.onProceedToFullScreen}
							/>
						</div>
					) : null}
				</div>
			</div>
		</header>
	)
}
